import classnames from 'classnames'
import styles from './AboutHero.module.scss'
import { useEffect, useRef, useState } from 'react'
import Link from '@/components/Link/Link'
import RichText from '@/components/RichText/RichText'
import Logo from '@/components/_svg/Logo'
import useStore from '@/store'
import { classesToElements, formatNumber } from '@/utils'
import gsap from 'gsap'
import AnimatedTextLines from '@/components/AnimatedTextLines/AnimatedTextLines'
import { TRANSITION_DURATION, TRANSITION_DURATION_MS } from '@/data'
import MovingContainer from '@/components/MovingContainer/MovingContainer'
import SanityImage from '@/components/SanityImage/SanityImage'
import useBreakpoint from '@/hooks/use-breakpoint'
import LineButton from '@/components/LineButton/LineButton'

const CLASSES = {
  STATIC_IMAGE_CONTAINER: styles.staticImageContainer,
  CATEGORIES_TITLE_TEXT: styles.categoriesContainer__titleText,
  CATEGORIES_LIST_ITEM_TEXT: styles.categoriesList__listItemText,
  DETAILS_TITLE_TEXT: styles.detailsContainer__titleText,
  DETAILS_LIST_ITEM_TEXT: styles.detailsList__listItemText,
  INDEX_CONTAINER_TEXT: styles.indexContainer__text,
  TITLE_TEXT: styles.title__text,
  SOCIALS_P: styles.socials__p,
  BACKGROUND_IMAGE_LEFT: styles.titleBackgroundImages__left,
  BACKGROUND_IMAGE_RIGHT: styles.titleBackgroundImages__right,
  LOGO: styles.logo,
}

const AboutHero = ({
  className,
  title,
  shortDescription,
  longDescription,
  categories,
  details,
  backgroundImageRight,
  backgroundImageLeft,
  detailsTitle,
  thirdColumnTitle,
  thirdColumnItems,
}) => {
  const globalData = useStore(state => state.globalData)
  const [categoryIndexHovered, setCategoryIndexHovered] = useState(0)
  const $container = useRef(null)
  const $refs = useRef({})
  const $shortDescriptionLines = useRef(null)
  const $longDescriptionLines = useRef(null)
  const $titleWords = useRef(null)
  const animations = useRef({})
  const loaderAnimationComplete = useStore(state => state.loaderAnimationComplete)
  const { isMobile } = useBreakpoint()
  const $socials = useRef(null)
  const [render, setRender] = useState(true)
  const renderDebounceTimeout = useRef(null)
  const hasRunAnimation = useRef(false)
  const theme = useStore(state => state.theme)
  const getAllElements = () => {
    if (!$container.current) return
    $refs.current = classesToElements(CLASSES, $container.current)
  }

  useEffect(() => {
    if (isMobile === null) return
    if (renderDebounceTimeout.current) {
      clearTimeout(renderDebounceTimeout.current)
    }

    hasRunAnimation.current = false
    setRender(false)

    renderDebounceTimeout.current = setTimeout(() => {
      setRender(true)
    }, 50)
  }, [isMobile])

  useEffect(() => {
    getAllElements()

    const animateIn = () => {
      getAllElements()

      const duration = 1
      const ease = 'Power3.easeOut'

      const {
        TITLE_TEXT,
        CATEGORIES_TITLE_TEXT,
        CATEGORIES_LIST_ITEM_TEXT,
        DETAILS_TITLE_TEXT,
        DETAILS_LIST_ITEM_TEXT,
        INDEX_CONTAINER_TEXT,
        SOCIALS_P,
        BACKGROUND_IMAGE_LEFT,
        BACKGROUND_IMAGE_RIGHT,
        LOGO,
      } = $refs.current

      gsap.to([BACKGROUND_IMAGE_LEFT, BACKGROUND_IMAGE_RIGHT], {
        opacity: 0.05,
        duration,
        stagger: 0.5,
        ease,
      })

      const animateInItem = (el, delay = 0) => {
        if (!el) return
        gsap.to(el, {
          y: 0,
          duration,
          ease,
          delay,
        })
      }

      const animateInList = (title, list, delay = 0) => {
        gsap.to([...list.reverse(), title], {
          y: 0,
          duration,
          ease,
          delay,
        })
      }

      gsap.killTweensOf(LOGO)
      gsap.to(LOGO, {
        opacity: 1,
        duration,
        ease,
        delay: 0.5,
      })

      animateInItem(TITLE_TEXT)
      animateInItem(INDEX_CONTAINER_TEXT)
      animateInItem(SOCIALS_P)
      animateInList(CATEGORIES_TITLE_TEXT, CATEGORIES_LIST_ITEM_TEXT)
      animateInList(DETAILS_TITLE_TEXT, DETAILS_LIST_ITEM_TEXT)

      if ($socials.current) {
        gsap.to($socials.current, {
          opacity: 1,
          duration,
          ease,
          delay: isMobile ? TRANSITION_DURATION : 0,
        })
      }

      if (!$titleWords.current || !$longDescriptionLines.current || !$shortDescriptionLines.current) return

      $titleWords.current.animate()
      $longDescriptionLines.current.animate()
      $shortDescriptionLines.current.animate()
    }

    animations.current = {
      animateIn,
    }
  }, [isMobile])

  useEffect(() => {
    if (!loaderAnimationComplete) return
    if (hasRunAnimation.current) return
    setTimeout(() => {
      if (!animations.current.animateIn) return
      animations.current.animateIn()
      hasRunAnimation.current = true
    }, TRANSITION_DURATION_MS * 0.4)
  }, [loaderAnimationComplete, render])

  if (
    !title ||
    !shortDescription ||
    !longDescription ||
    !categories ||
    !details ||
    !globalData?.navigation?.instagramLink ||
    !globalData?.navigation?.email ||
    !backgroundImageRight ||
    !backgroundImageLeft
  ) {
    return null
  }

  if (!render) return null

  const categoryDetailsContent = (
    <div className={styles.categoriesDetailsContainer}>
      <div className={styles.categoriesContainer}>
        <p className={styles.categoriesContainer__title}>
          <span className={CLASSES.CATEGORIES_TITLE_TEXT}>Categories:</span>
        </p>
        <ul className={styles.categoriesList}>
          {categories.map((item, index) => (
            <li
              className={styles.categoriesList__listItem}
              onMouseEnter={() => {
                setCategoryIndexHovered(index)
              }}
              key={index}
            >
              <span className={CLASSES.CATEGORIES_LIST_ITEM_TEXT}>{item.category.title}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.detailsContainer}>
        <p className={styles.detailsContainer__title}>
          <span className={CLASSES.DETAILS_TITLE_TEXT}>{detailsTitle}:</span>
        </p>
        <ul className={styles.detailsList}>
          {details.map((link, i) => (
            <li
              key={i}
              className={styles.detailsList__listItem}
            >
              <span className={CLASSES.DETAILS_LIST_ITEM_TEXT}>
                <Link link={link} />
              </span>
            </li>
          ))}
        </ul>
      </div>
      {thirdColumnTitle && (
        <div className={styles.thirdCol}>
          <p className={styles.thirdCol__title}>
            <span className={CLASSES.DETAILS_TITLE_TEXT}>{thirdColumnTitle}:</span>
          </p>
          {!!thirdColumnItems?.length && (
            <ul className={styles.thirdColList}>
              {thirdColumnItems.map((item, i) => (
                <li
                  key={i}
                  className={styles.thirdColList__listItem}
                >
                  <span className={CLASSES.DETAILS_LIST_ITEM_TEXT}>
                    {item?.link?.linkType !== 'disabled' ? (
                      <Link
                        link={{
                          ...item?.link,
                        }}
                      >
                        {item.title}
                      </Link>
                    ) : (
                      <span>{item.title}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )

  const imageAndIndexContainer = (
    <div className={styles.imageAndIndexContainer}>
      <p className={styles.indexContainer}>
        <span className={CLASSES.INDEX_CONTAINER_TEXT}>({formatNumber(categoryIndexHovered + 1, 2)})</span>
      </p>
      <div className={styles.images}>
        {categories.map((item, i) => (
          <div
            key={i}
            className={classnames(styles.images__imageContainer, {
              [styles.active]: i === categoryIndexHovered,
            })}
          >
            <SanityImage
              image={item.productExample.image}
              className={styles.images__image}
              disableLoadedOpacity
              breakpoints={{
                tablet: {
                  width: 400,
                },
                xs: {
                  width: 400,
                },
              }}
            />
          </div>
        ))}
      </div>
    </div>
  )

  const shortDescriptionContent = (
    <div className={styles.shortDescription}>
      <AnimatedTextLines
        toAnimate="lines"
        querySelector="p"
        animateInConfig={{
          duration: 1.2,
          stagger: 0.05,
        }}
        autoAnimate={false}
        ref={ref => {
          $shortDescriptionLines.current = ref
        }}
      >
        <RichText content={shortDescription} />
      </AnimatedTextLines>
    </div>
  )

  const titleContainerContent = (
    <div className={styles.titleContainer}>
      <MovingContainer
        strengthMultiplier={-0.1}
        className={styles.titleBackgroundImages}
      >
        <SanityImage
          image={backgroundImageLeft}
          className={CLASSES.BACKGROUND_IMAGE_LEFT}
          disableLoadedOpacity
          breakpoints={{
            tablet: {
              width: 200,
            },
            xs: {
              width: 100,
            },
          }}
        />
        <SanityImage
          image={backgroundImageRight}
          className={CLASSES.BACKGROUND_IMAGE_RIGHT}
          disableLoadedOpacity
          breakpoints={{
            tablet: {
              width: 200,
            },
            xs: {
              width: 100,
            },
          }}
        />
      </MovingContainer>
      <MovingContainer
        className={styles.titleTextContainer}
        strengthMultiplier={-0.2}
      >
        <AnimatedTextLines
          toAnimate="words"
          querySelector="span"
          animateInConfig={{
            duration: 1.5,
            stagger: 0.1,
          }}
          autoAnimate={false}
          ref={ref => {
            $titleWords.current = ref
          }}
        >
          <span className={CLASSES.TITLE_TEXT}>{title}</span>
        </AnimatedTextLines>
      </MovingContainer>
    </div>
  )

  const logoContent = <Logo className={CLASSES.LOGO} />

  const socialsContent = (
    <div
      className={styles.socials}
      ref={$socials}
    >
      <p className={CLASSES.SOCIALS_P}>
        <span className={styles.socials__letter}>e:</span>
        <LineButton
          label={'Get in Touch'}
          showInitially={true}
          link={{ linkType: 'external', link: `mailto:${globalData?.navigation?.email}` }}
          theme={theme}
        />
      </p>
      <p className={CLASSES.SOCIALS_P}>
        <span className={styles.socials__letter}>s:</span>
        <LineButton
          label={'Instagram'}
          showInitially={true}
          link={{
            linkType: 'external',
            link: globalData?.navigation?.instagramLink,
          }}
          theme={theme}
        />
      </p>
    </div>
  )

  const longDescriptionContent = (
    <div className={styles.longDescription}>
      <AnimatedTextLines
        toAnimate="lines"
        querySelector="p"
        animateInConfig={{
          duration: 1.2,
          stagger: 0.05,
        }}
        autoAnimate={false}
        ref={ref => {
          $longDescriptionLines.current = ref
        }}
      >
        <RichText content={longDescription} />
      </AnimatedTextLines>
    </div>
  )

  return (
    <div
      ref={$container}
      className={classnames(styles.AboutHero, className)}
    >
      <div className={styles.inner}>
        {isMobile ? (
          <>
            {shortDescriptionContent}
            {longDescriptionContent}
            {imageAndIndexContainer}
            {categoryDetailsContent}
            {titleContainerContent}
            {logoContent}
            {socialsContent}
          </>
        ) : (
          <>
            <div className={styles.leftSide}>
              {categoryDetailsContent}
              {imageAndIndexContainer}
            </div>
            <div className={styles.centerSide}>
              {shortDescriptionContent}
              {titleContainerContent}
              {logoContent}
            </div>
            <div className={styles.rightSide}>
              {longDescriptionContent}
              {socialsContent}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

AboutHero.displayName = 'AboutHero'

export default AboutHero
